import Amplitude from "@/shared/services/amplitude.service";
import { useDataStore } from "@/shared/store/useDataStore";
import useModalStore, { AuthModalTypeEnum } from "@/shared/store/useModalStore";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { isBrowser } from "@/shared/utils/dom";
import { scheduleDemoLink } from "@/shared/utils/route.util";
import {
  appendQueryParamsToLocalStorage,
  getFromLocal,
} from "@/shared/utils/storage.util";
import {
  getUTMFieldsFromLocalStorage,
  stringifyUTMParams,
} from "@/shared/utils/utm.util";
import classNames from "classnames";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { parseCookies } from "nookies";
import NProgress from "nprogress";
import { MouseEvent } from "react";
import Button, { ButtonSizeEnum, ButtonTypeEnum } from "../atoms/Button";

export enum CtaActionType {
  SignUp = "SignUp",
  BookDemo = "BookDemo",
  DelayedHrefBlank = "DelayedHrefBlank",
  DelayedHref = "DelayedHref",
  RedirectHref = "redirectHref",
  SecureMySpot = "SecureMySpot",
  ScheduleADemo = "ScheduleADemo",
  NoAction = "",
}

export enum BookADemoSourceEnum { // sources of book a demo
  hero = "hero",
  bodyEnd = "body_end",
  pricing_section = "pricing_section",
  metrics_section = "metrics_section",
  features_section = "features_section",
}

export enum ScheduleADemoSourceEnum {
  navbar = "navbar",
  hero = "hero",
  features_section = "features_section",
  competitor_comparison = "competitor_comparison",
  pricing_plans = "pricing_plans",
  body_end = "body_end",
  promotion_strip = "promotion_strip",
  logo = "logo",
}

export enum LearnMoreSourceEnum {
  products_section = "products_section",
  assist_section = "assist_section",
  ai_avatar="ai_avatar",
}

export enum ExploreCreatorsSourceEnum {
  body = "body",
  navbar = "navbar",
}

export enum SignUpSourceEnum {
  hero = "hero",
  body_end = "body_end",
  products_section = "products_section",
  assist_section = "assist_section",
  metrics_section = "metrics_section",
  pricing_section = "pricing_section",
  features_section = "features_section",
  earnings_slider_section = "earnings_slider_section",
  marketing_features = "marketing_features",
  bw_creator_cards = "bw_creator_cards",
  ai_avatar= "avatar_section",
}

export type CtaType = {
  type: ButtonTypeEnum;
  size: ButtonSizeEnum;
  text: string;
  actionType?: CtaActionType;
  href?: string;
  target?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  amplitudeEvent?: {
    eventName: string;
    eventProperties: { [key: string]: any };
  };
  queryParams?: { [key: string]: any };
  utmFields?: { [key: string]: string };
};

type ButtonPropsType = {
  ctas: CtaType[];
  gap?: string;
  fullWidthOnMobile?: boolean;
  align?: "sm:center" | "md:center" | "sm:left" | "md:left" | "stretch";
  wideButton?: boolean;
  fullWidth?: Boolean;
  affiliateButton?: Boolean
};

const CtaButtonGroup = ({
  ctas,
  gap = "gap-3",
  fullWidthOnMobile = true,
  wideButton = false,
  fullWidth = false,
  align = "sm:center",
  affiliateButton = false
}: ButtonPropsType) => {
  // console.log(align, ctas[0].text);
  const { openAuthModal } = useModalStore();
  const router = useRouter();
  const { openScheduleFormOnScheduleCTAClick, setShowScheduleDemoModal } =
    useDataStore();

  const params = useSearchParams();
  const gclid = params.get("gclid");

  const { setOpenSignUp } = useModalStore();
  const handleBookDemo = (cta: CtaType) => {
    if (openScheduleFormOnScheduleCTAClick) {
      setShowScheduleDemoModal(true);
    } else {
      const currentUrl = router.pathname;
      let extraUtmFields = cta?.utmFields ?? {};
      extraUtmFields["utm_content"] = gclid ? gclid : "autobookdemos";
      scheduleDemoLink({ extraUtmFields: cta.utmFields });
    }
  };
  // Absolute URLs must bypass router.push: on same-origin, Next.js extracts the
  // pathname and re-applies the current locale (e.g. https://graphy.com/signup/
  // becomes /in/signup/), which the /in/ middleware then redirects to /.
  const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

  const handleDelayedHref = (ctaHref: string) => {
    NProgress.start();
    setTimeout(() => {
      if (isAbsoluteUrl(ctaHref)) {
        window.location.href = ctaHref;
      } else {
        router.push(ctaHref);
      }
    }, 500);
  };

  const handleRedirectHref = (ctaHref: string) => {
    NProgress.start();
    if (isAbsoluteUrl(ctaHref)) {
      window.location.href = ctaHref;
    } else {
      router.push(ctaHref);
    }
  };

  const handleRedirectHrefBlank = (ctaHref: string) => {
    if (isBrowser) {
      const cookies = parseCookies();
      let utmFields = getUTMFieldsFromLocalStorage();
      utmFields["utm_content"] = gclid ? gclid : "autobookdemos";
      const utmParams = stringifyUTMParams(utmFields);

      setTimeout(() => {
        window
          ?.open(
            `${ctaHref}&${cookies.countryCode !== "KR" ? utmParams : ""}`,
            "_blank"
          )
          ?.focus();
      }, 500);
    }
  };

  const handleSecureMySpot = () => {
    const scrollY = window.scrollY;
    if (scrollY === 0) {
      window.scrollTo({ top: 80, behavior: "smooth" });
    } else if (scrollY > 80) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleScheduleADemo = () => {
    const scrollY = window.scrollY;
    if (scrollY === 0) {
      window.scrollTo({ top: 80, behavior: "smooth" });
    } else if (scrollY > 80) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };


  const handleActionClick = (cta: CtaType) => {
    if (cta.amplitudeEvent) {
      new Amplitude().sendEvent({
        type: cta.amplitudeEvent.eventName,
        properties: {
          ...cta.amplitudeEvent.eventProperties,
          current_url: getCurrentURL(),
          utm_params: getFromLocal("utmFields"),
          screen_width: getScreenWidth(),
          referrer: getReferrer(),
        } as { [key: string]: string | number },
      });
    }
    if (cta.queryParams) {
      appendQueryParamsToLocalStorage(cta.queryParams ?? {});
    }
    switch (cta.actionType) {
      case CtaActionType.SignUp: {
        if (isBrowser) {
          const prefix =
            router.locale && router.locale !== router.defaultLocale
              ? `/${router.locale}`
              : "";
          window.open(`${prefix}/signup`, "_blank")?.focus();
        }
        break;
      }
      case CtaActionType.BookDemo: {
        handleBookDemo(cta);
        break;
      }
      case CtaActionType.DelayedHref: {
        if (cta.href) handleDelayedHref(cta.href);
        break;
      }
      case CtaActionType.RedirectHref: {
        if (cta.href) handleRedirectHref(cta.href);
        break;
      }
      case CtaActionType.DelayedHrefBlank: {
        if (cta.href) handleRedirectHrefBlank(cta.href);
        break;
      }
      case CtaActionType.SecureMySpot: {
        handleSecureMySpot()
      }
      case CtaActionType.ScheduleADemo: {
        handleScheduleADemo() 
      }
    }
  };

  return (
    <div
      className={classNames(
        {
          "grid sm:inline-grid": fullWidthOnMobile,
          "inline-grid": !fullWidthOnMobile,
        },
        gap,
        `flex-grow self-stretch sm:flex-grow-0 ${
          fullWidth
            ? "w-full"
            : wideButton
            ? "w-[146px]"
            : affiliateButton
            ? "w-full md:w-[390px] "
            : ""
        }`,
        {
          "sm:self-center": align === "sm:center",
          "md:self-center": align === "md:center",
          "sm:self-start": align === "sm:left",
          "md:self-start": align === "md:left",
          "!flex-grow !self-stretch": align === "stretch",
        }
      )}
      style={{
        // `minmax(0, 1fr)` (instead of plain `1fr`) makes every track
        // equal-width even when a CTA's content is wider than its share —
        // without this the longer button's auto min-content size pushes
        // its track and the two CTAs render at different widths.
        gridTemplateColumns: `repeat(${ctas?.length}, minmax(0, 1fr))`,
      }}
    >
      {ctas?.map((cta, index) => (
        <Button
          key={index}
          type={cta.type}
          size={cta.size}
          onClick={cta.actionType ? () => handleActionClick(cta) : cta.onClick}
          cta={cta}
          href={
            // If CtaActionType.DelayedHref, then handling the href with router.push
            cta.actionType === CtaActionType.DelayedHref ||
            cta.actionType === CtaActionType.DelayedHrefBlank
              ? undefined
              : cta.href
          }
          target={cta.target}
          clickId={cta.actionType === CtaActionType.SignUp ? "signup-button" : undefined}
        >
          {cta.text}
        </Button>
      ))}
    </div>
  );
};

export default CtaButtonGroup;
